/**
 * puur-via-slider-fix.js
 * Herstelt de mobiele Flickity-hoofdslider op de PDP wanneer die na een
 * variantwissel (StarApps VIA vervangt de media-gallery) met size 0 is
 * geinitialiseerd en daardoor een lege inline transform heeft (foto klapt in).
 *
 * Repair: kaal instance.resize() (fase B stap 0 bewezen: herstelt transform,
 * cell-sizes en foto volledig; wiring is bij dit defect al gezond).
 * Scoped per gallery, predicate-gated, retry-begrensd. Geen document-events.
 *
 * Plan: docs-puur-flickity-fix/plans/2026-08-10-puur-variant-foto-flickity-fix.md
 */
(function () {
  "use strict";

  // Guard tegen dubbele executie binnen een documentleven (Codex 3)
  if (window.__puurViaSliderFix) return;
  window.__puurViaSliderFix = true;

  var DEBUG = false;
  var TAG = "[puur-via-slider-fix]";
  var MOBILE_QUERY = "screen and (max-width: 767px)"; // exact theme-script.liquid
  var SECTION_SELECTOR = 'product-info[data-section-type="main-product"]';
  var DEBOUNCE_MS = 100;
  var LATE_CHECK_MS = 1200;

  function log() {
    if (!DEBUG) return;
    try {
      var args = Array.prototype.slice.call(arguments);
      args.unshift(TAG);
      console.debug.apply(console, args);
    } catch (e) { /* fail-silent */ }
  }

  // Fouten altijd zichtbaar (DEBUG-onafhankelijk), maar nooit throwen (Codex 6)
  function warn() {
    try {
      var args = Array.prototype.slice.call(arguments);
      args.unshift(TAG);
      console.warn.apply(console, args);
    } catch (e) { /* fail-silent */ }
  }

  var generation = 0;
  var debounceTimer = null;
  var retriedGeneration = -1; // max 1 retry per generation
  var observer = null;
  var section = null;

  function isMobile() {
    return window.matchMedia && window.matchMedia(MOBILE_QUERY).matches;
  }

  // Gallery-referentie ALTIJD per trigger opnieuw resolven (node-identiteit
  // wisselt bij variantwissel), nooit een oude referentie hergebruiken.
  function resolveGallery() {
    if (!section || !section.isConnected) return null;
    var mg = section.querySelector("media-gallery");
    if (!mg || !mg.isConnected) return null;
    return mg;
  }

  /**
   * Semantische defectpredicate (plan v2.1).
   * Retourneert { broken: bool, reason: string } en logt de beslissing.
   * Fail-closed: elke twijfel of ontbrekende voorwaarde = skip (broken=false).
   */
  function evaluatePredicate(mg) {
    var verdict = function (broken, reason, extra) {
      log("predicate:", broken ? "KAPOT" : "ok/skip", "-", reason, extra || "");
      return { broken: broken, reason: reason };
    };

    // Skip-regels (plan predicate 3)
    if (!isMobile()) return verdict(false, "skip: desktop");
    if (!mg) return verdict(false, "skip: geen gallery");
    if (!section.querySelector("[data-variant-picker]")) {
      return verdict(false, "skip: geen variant-picker");
    }
    if (mg.offsetParent === null && mg.getClientRects().length === 0) {
      return verdict(false, "skip: gallery verborgen");
    }
    // Custom element nog niet geinitialiseerd: wachten op volgende trigger
    if (!mg.domNodes || !mg.domNodes.mediaGallery) {
      return verdict(false, "skip: gallery nog niet geinitialiseerd");
    }

    var viewer = mg.querySelector('[id^="GalleryViewer"]');
    // NB (Codex 5): dit telt aanwezige, niet strikt "zichtbare" slides. Een
    // onterecht eligible geval leidt hooguit tot een kaal resize() op een
    // gezonde slider — idempotent en onschadelijk; skip blijft fail-closed.
    var slides = viewer
      ? viewer.querySelectorAll(".f-product__media")
      : mg.querySelectorAll(".f-product__media");
    if (slides.length <= 1) return verdict(false, "skip: <=1 slide");

    var inst =
      mg.mediaGallery ||
      (mg.domNodes.mediaGallery.slider && mg.domNodes.mediaGallery.slider.instance);

    // Branch (a): instantie ontbreekt terwijl hij hoort te bestaan
    if (!inst || typeof inst !== "object") {
      return verdict(true, "branch a: instantie ontbreekt");
    }

    var sliderEl = mg.querySelector(".flickity-slider");
    if (!sliderEl) return verdict(true, "branch a: .flickity-slider ontbreekt");

    // Branch (b): cells[]-volgorde != DOM-volgorde
    // (ook 0 cells vs >0 DOM-cellen telt als KAPOT, Codex 4)
    try {
      if (inst.cells) {
        var domCells = Array.prototype.slice.call(sliderEl.children);
        if (inst.cells.length !== domCells.length) {
          return verdict(true, "branch b: cells-aantal != DOM", {
            cells: inst.cells.length,
            dom: domCells.length,
          });
        }
        for (var i = 0; i < inst.cells.length; i++) {
          if (inst.cells[i].element !== domCells[i]) {
            return verdict(true, "branch b: cells-volgorde != DOM", { index: i });
          }
        }
      }
    } catch (e) {
      warn("predicate branch b error (skip):", e);
      return verdict(false, "skip: branch b onleesbaar");
    }

    // Branch (c): inline transform leeg/afwezig bij geinitialiseerd, >1 slide
    var transform = sliderEl.style.transform;
    if (!transform || transform === "" || transform === "none") {
      return verdict(true, "branch c: lege inline transform", {
        sizeWidth: inst.size && inst.size.width,
      });
    }

    return verdict(false, "gezond");
  }

  /**
   * Repair: kaal resize() op de hoofdslider-instantie (+ thumbnails,
   * onschadelijk en houdt beide administraties in sync).
   */
  function applyRepair(mg) {
    var inst =
      mg.mediaGallery ||
      (mg.domNodes &&
        mg.domNodes.mediaGallery &&
        mg.domNodes.mediaGallery.slider &&
        mg.domNodes.mediaGallery.slider.instance);
    if (!inst || typeof inst.resize !== "function") {
      warn("repair: geen instantie met resize(), afgebroken");
      return false;
    }
    inst.resize();
    try {
      if (mg.thumbnails && typeof mg.thumbnails.resize === "function") {
        mg.thumbnails.resize();
      }
    } catch (e) {
      warn("repair: thumbnails.resize faalde (genegeerd):", e);
    }
    log("repair: resize() uitgevoerd");
    return true;
  }

  // rAF met fallback, zodat re-attach nooit van rAF-beschikbaarheid afhangt
  function nextFrame(cb) {
    if (typeof window.requestAnimationFrame === "function") {
      window.requestAnimationFrame(cb);
    } else {
      setTimeout(cb, 16);
    }
  }

  // Hardening (Codex 2): is de section-node vervangen/detached, dan opnieuw
  // resolven en de observer verhangen. Triggers blijven dan binnenkomen via
  // het document-brede product-media:loaded-event.
  function ensureSection() {
    if (section && section.isConnected) return true;
    var fresh = document.querySelector(SECTION_SELECTOR);
    if (!fresh) {
      log("section niet (meer) aanwezig, skip");
      return false;
    }
    log("section-node vervangen, observer verhangen");
    section = fresh;
    disconnectObserver();
    attachObserver();
    return true;
  }

  function runCheck(gen, isRetry) {
    try {
      if (gen !== generation) {
        log("check: generation verouderd (", gen, "!=", generation, "), skip");
        return;
      }
      if (!ensureSection()) return;
      var mg = resolveGallery();
      var result = evaluatePredicate(mg);
      if (!result.broken) return;

      // Observer los tijdens repair; re-attach GEGARANDEERD via finally +
      // nextFrame (Codex 1: een throw mag de observer nooit uit laten staan)
      disconnectObserver();
      var repaired = false;
      try {
        repaired = applyRepair(mg);
      } catch (e) {
        warn("repair error:", e);
      } finally {
        nextFrame(function () {
          attachObserver();
          try {
            if (gen !== generation) return; // nieuwe burst neemt het over
            var mg2 = resolveGallery();
            var after = evaluatePredicate(mg2);
            if (after.broken && repaired) {
              if (!isRetry && retriedGeneration !== gen) {
                retriedGeneration = gen;
                log("na repair nog kapot, 1 retry (gen", gen, ")");
                runCheck(gen, true);
              } else {
                warn("na retry nog kapot, stop voor gen", gen, "-", after.reason);
              }
            } else if (!after.broken) {
              log("repair geverifieerd gezond (gen", gen, ")");
            }
          } catch (e) {
            warn("post-repair check error:", e);
          }
        });
      }
    } catch (e) {
      warn("runCheck error (fail-silent):", e);
    }
  }

  function scheduleCheck(source) {
    try {
      generation++;
      var gen = generation;
      log("trigger:", source, "-> gen", gen);
      if (debounceTimer) clearTimeout(debounceTimer);
      debounceTimer = setTimeout(function () {
        debounceTimer = null;
        runCheck(gen, false);
      }, DEBOUNCE_MS);
    } catch (e) {
      warn("scheduleCheck error:", e);
    }
  }

  // Pre-paint sync repair (glitch-patch 2026-08-10): de observer-callback
  // draait als microtask vóór de volgende paint; een synchrone resize() hier
  // voorkomt dat het kapotte frame ooit geschilderd wordt (baseline: ~290ms
  // zichtbaar defect door de 100ms-debounce). Geverifieerd op preview:
  // resize() genereert 0 childList-records binnen de section, dus de observer
  // hoeft hiervoor niet los. Het debounced pad blijft ongewijzigd bestaan als
  // verify/vangnet (incl. generation/retry-administratie).
  var fixing = false;
  function syncRepair() {
    if (fixing) return; // re-entrancy guard
    fixing = true;
    try {
      if (!section || !section.isConnected) return;
      var mg = resolveGallery();
      var result = evaluatePredicate(mg);
      if (result.broken) {
        log("sync-repair (pre-paint), gen", generation);
        applyRepair(mg);
      }
    } catch (e) {
      warn("syncRepair error:", e);
    } finally {
      fixing = false;
    }
  }

  function attachObserver() {
    try {
      if (!section || !section.isConnected) return;
      if (!observer) {
        observer = new MutationObserver(function () {
          syncRepair();
          scheduleCheck("mutation");
        });
      }
      observer.observe(section, { childList: true, subtree: true });
    } catch (e) {
      warn("attachObserver error:", e);
    }
  }

  function disconnectObserver() {
    try {
      if (observer) observer.disconnect();
    } catch (e) { /* fail-silent */ }
  }

  function init() {
    try {
      // Eligibility-gate (fail-closed): alles moet waar zijn, anders inert.
      section = document.querySelector(SECTION_SELECTOR);
      if (!section) {
        log("gate: geen hoofd-PDP-section, hook inert");
        return;
      }
      if (!section.querySelector("media-gallery")) {
        log("gate: geen media-gallery, hook inert");
        return;
      }
      if (!section.querySelector("[data-variant-picker]")) {
        log("gate: geen variant-picker, hook inert");
        return;
      }
      if (!window.matchMedia || !window.MutationObserver) {
        log("gate: browser-API ontbreekt, hook inert");
        return;
      }
      log("gate: gepasseerd, observer attach op", SECTION_SELECTOR);

      attachObserver();

      // Initial-load-checks: DOM-ready (nu), product-media:loaded, late check
      scheduleCheck("dom-ready");
      document.addEventListener("product-media:loaded", function () {
        scheduleCheck("product-media:loaded");
      });
      setTimeout(function () {
        scheduleCheck("late-initial-check");
      }, LATE_CHECK_MS);
    } catch (e) {
      warn("init error (hook inert):", e);
    }
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", init);
  } else {
    init();
  }
})();
